import {
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Avatar,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";

import type { UserRole, UserStatus } from "../api/auth";
import { ApiError } from "../api/client";
import {
  createUser,
  fetchUsers,
  resetUserPassword,
  updateUser,
  type UserListItem,
} from "../api/users";
import { passwordStrengthError } from "../utils/validation";

const ROLE_LABELS: Record<UserRole, string> = {
  NORMAL_USER: "普通用户",
  REVIEWER: "审核员",
  ADMIN: "管理员",
};
const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));
const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "正常" },
  { value: "DISABLED", label: "已停用" },
];

interface UserFormValues {
  username: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  initialPassword: string;
}

export function UserManagementPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<UserFormValues>();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState<UserRole>();
  const [status, setStatus] = useState<UserStatus>();
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const query = useQuery({
    queryKey: ["admin-users", page, search, role, status],
    queryFn: ({ signal }) => fetchUsers({ page, pageSize: 20, search, role, status }, signal),
  });
  const saveMutation = useMutation({
    mutationFn: (values: UserFormValues) =>
      editing
        ? updateUser(editing.id, {
            name: values.name,
            role: values.role,
            status: values.status,
            version: editing.version,
          })
        : createUser({
            username: values.username,
            name: values.name,
            role: values.role,
            initial_password: values.initialPassword,
          }),
    onSuccess: async () => {
      message.success(editing ? "用户资料已更新" : "用户已创建，首次登录需修改初始密码");
      setDrawerOpen(false);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.message : "保存失败"),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ role: "NORMAL_USER", status: "ACTIVE" });
    setDrawerOpen(true);
  };
  const openEdit = (user: UserListItem) => {
    setEditing(user);
    form.resetFields();
    form.setFieldsValue({
      username: user.username,
      name: user.name,
      role: user.role,
      status: user.status,
    });
    setDrawerOpen(true);
  };

  const showResetPassword = (user: UserListItem) => {
    let nextPassword = "";
    Modal.confirm({
      title: `重置 ${user.name} 的密码`,
      content: (
        <div className="modal-field">
          <Typography.Text type="secondary">重置后该用户所有现有会话会立即失效。</Typography.Text>
          <Input.Password
            autoComplete="new-password"
            placeholder="至少 12 位，满足三类字符"
            onChange={(event) => {
              nextPassword = event.target.value;
            }}
          />
        </div>
      ),
      okText: "确认重置",
      okButtonProps: { danger: true },
      async onOk() {
        const validationError = passwordStrengthError(nextPassword);
        if (validationError) {
          message.error(validationError);
          throw new Error(validationError);
        }
        try {
          await resetUserPassword(user.id, nextPassword);
          message.success("密码已重置");
          await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
        } catch (error) {
          message.error(error instanceof ApiError ? error.message : "密码重置失败");
          throw error;
        }
      },
    });
  };

  return (
    <main className="management-page">
      <div className="page-heading">
        <div>
          <Typography.Text className="section-eyebrow">IDENTITY & ACCESS</Typography.Text>
          <Typography.Title level={1}>用户管理</Typography.Title>
          <Typography.Paragraph type="secondary">
            维护独立登录账号、姓名、角色与账号状态。
          </Typography.Paragraph>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建用户
        </Button>
      </div>
      <Card className="data-card" variant="borderless">
        <div className="table-toolbar">
          <Input.Search
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜索用户名或姓名"
            onSearch={(value) => {
              setSearch(value.trim());
              setPage(1);
            }}
          />
          <Select<UserRole>
            allowClear
            placeholder="全部角色"
            options={ROLE_OPTIONS}
            onChange={(value) => {
              setRole(value);
              setPage(1);
            }}
          />
          <Select<UserStatus>
            allowClear
            placeholder="全部状态"
            options={STATUS_OPTIONS}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
          />
        </div>
        <Table<UserListItem>
          rowKey="id"
          loading={query.isPending}
          dataSource={query.data?.items}
          scroll={{ x: 980 }}
          pagination={{
            current: page,
            pageSize: 20,
            total: query.data?.total,
            showTotal: (total) => `共 ${total} 个账号`,
            onChange: setPage,
          }}
          columns={[
            {
              title: "用户",
              key: "identity",
              width: 230,
              render: (_, user) => (
                <div className="identity-cell">
                  <Avatar src={user.avatar_url} icon={<UserOutlined />}>
                    {user.avatar_text}
                  </Avatar>
                  <span>
                    <strong>{user.name}</strong>
                    <small>@{user.username}</small>
                    {user.is_core_account ? <Tag color="purple">核心账号</Tag> : null}
                  </span>
                </div>
              ),
            },
            {
              title: "角色",
              dataIndex: "role",
              width: 120,
              render: (value: UserRole) => (
                <Tag color={value === "ADMIN" ? "blue" : value === "REVIEWER" ? "gold" : "default"}>
                  {ROLE_LABELS[value]}
                </Tag>
              ),
            },
            {
              title: "状态",
              dataIndex: "status",
              width: 110,
              render: (value: UserStatus) => (
                <Tag color={value === "ACTIVE" ? "success" : "error"}>
                  {value === "ACTIVE" ? "正常" : "已停用"}
                </Tag>
              ),
            },
            {
              title: "初始密码",
              dataIndex: "must_change_password",
              width: 120,
              render: (value: boolean) =>
                value ? (
                  <Tag color="warning">待修改</Tag>
                ) : (
                  <Typography.Text type="secondary">已更新</Typography.Text>
                ),
            },
            {
              title: "创建时间",
              dataIndex: "created_at",
              width: 180,
              render: (value: string) => new Date(value).toLocaleString("zh-CN", { hour12: false }),
            },
            {
              title: "操作",
              key: "actions",
              fixed: "right",
              width: 180,
              render: (_, user) => (
                <Space>
                  <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(user)}>
                    编辑
                  </Button>
                  <Button
                    type="link"
                    icon={<KeyOutlined />}
                    disabled={user.is_core_account}
                    onClick={() => showResetPassword(user)}
                  >
                    重置密码
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Drawer
        title={editing ? "编辑用户" : "新建用户"}
        size={460}
        open={drawerOpen}
        destroyOnHidden
        onClose={() => setDrawerOpen(false)}
        extra={
          <Button type="primary" loading={saveMutation.isPending} onClick={() => form.submit()}>
            保存
          </Button>
        }
      >
        <Form<UserFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <Form.Item
            name="username"
            label="登录用户名"
            extra="4–32 位，仅允许字母、数字、点、下划线和连字符"
            rules={[{ required: !editing, message: "请输入登录用户名" }]}
          >
            <Input disabled={Boolean(editing)} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, whitespace: true, message: "请输入姓名" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select options={ROLE_OPTIONS} disabled={Boolean(editing?.is_core_account)} />
          </Form.Item>
          {editing ? (
            <Form.Item name="status" label="账号状态" rules={[{ required: true }]}>
              <Select options={STATUS_OPTIONS} disabled={Boolean(editing?.is_core_account)} />
            </Form.Item>
          ) : null}
          {!editing ? (
            <Form.Item
              name="initialPassword"
              label="初始密码"
              extra="12–128 位，需包含大小写字母、数字、符号中的至少三类"
              rules={[
                { required: true, message: "请输入初始密码" },
                {
                  validator: (_, value: string | undefined) => {
                    if (!value) return Promise.resolve();
                    const validationError = passwordStrengthError(value);
                    return validationError
                      ? Promise.reject(new Error(validationError))
                      : Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          ) : null}
        </Form>
      </Drawer>
    </main>
  );
}
